// FeedbackEditor (Unit 36e).
//
// Bottom-of-panel textarea bound to `papers.feedback_text`. Auto-saves on
// blur. The collapsed/expanded distinction makes the panel feel light when
// the field is unused but expands inline when there's content.

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { apiJson } from "../api/client";
import type { Paper } from "../api/types";

import "./FeedbackEditor.css";

interface Props {
  paper: Paper;
  onPatched: (paper: Paper) => void;
}

export function FeedbackEditor({ paper, onPatched }: Props) {
  const { t } = useTranslation();
  const [value, setValue] = useState(paper.feedback_text ?? "");
  const [editing, setEditing] = useState((paper.feedback_text ?? "") === "");
  const [saving, setSaving] = useState(false);

  // If the paper changes (or the server-side value drifts), resync local state.
  useEffect(() => {
    setValue(paper.feedback_text ?? "");
  }, [paper.id, paper.feedback_text]);

  async function persist() {
    const trimmed = value.trim();
    if (trimmed === (paper.feedback_text ?? "")) {
      setEditing(trimmed === "");
      return;
    }
    setSaving(true);
    try {
      const updated = await apiJson<Paper>(`/api/papers/${paper.id}`, {
        method: "PATCH",
        body: JSON.stringify({ feedback_text: trimmed === "" ? null : trimmed }),
      });
      onPatched(updated);
      setEditing(trimmed === "");
    } catch {
      // Re-show editing state so the user can retry.
    } finally {
      setSaving(false);
    }
  }

  const hasValue = (paper.feedback_text ?? "").trim().length > 0;

  if (!editing && hasValue) {
    return (
      <div className="feedback-editor feedback-editor--collapsed">
        <div className="feedback-editor__label">{t("paper_review.feedback_label")}</div>
        <div className="feedback-editor__preview" onClick={() => setEditing(true)}>
          {paper.feedback_text}
        </div>
        <button
          type="button"
          className="feedback-editor__edit"
          onClick={() => setEditing(true)}
        >
          {t("paper_review.feedback_edit")}
        </button>
      </div>
    );
  }

  return (
    <div className="feedback-editor">
      <label className="feedback-editor__label" htmlFor={`feedback-${paper.id}`}>
        {t("paper_review.feedback_label")}
      </label>
      <textarea
        id={`feedback-${paper.id}`}
        rows={3}
        value={value}
        placeholder={t("paper_review.feedback_placeholder")}
        onChange={(e) => setValue(e.target.value)}
        onBlur={() => void persist()}
        disabled={saving}
      />
    </div>
  );
}
